import { useQuery } from "@tanstack/react-query";
import { Badge, Dot, EmptyState, ErrorState, Grid, PageHeader, Panel, Skeleton, SkeletonRegion, Stack } from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Stat } from "../components/foundation";
import { AnimatedNumber } from "../lib/motion";

type ServiceStatus = {
  name: string;
  status: "ok" | "down" | "unknown";
  detail?: string;
};
type StatusResponse = { services: ServiceStatus[]; checkedAt: string };

async function fetchStatus(): Promise<StatusResponse> {
  const res = await fetch("/api/status");
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json();
}

export function PlatformStatus() {
  const { data, isLoading, isError, dataUpdatedAt, refetch } = useQuery({
    queryKey: ["status"],
    queryFn: fetchStatus,
    refetchInterval: 5000,
  });

  const services = data?.services ?? [];
  const total = services.length;
  const up = services.filter((s) => s.status === "ok").length;
  const down = total - up;
  const allOk = total > 0 && down === 0;
  const checked = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString("en-GB", { hour12: false })
    : "—";

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Platform status"
        description="Live health of every service in the stack - one compose stack, single server, single image, four commands. Polled every 5s."
        action={
          <Badge variant={toneVariant(allOk ? "ok" : down > 0 ? "crit" : "idle")}>
            <Dot tone={allOk ? "ok" : down > 0 ? "crit" : undefined} live={allOk} />
            {allOk ? "all systems ok" : down > 0 ? `${down} down` : "checking…"}
          </Badge>
        }
      />

      <Grid cols={4} gap="md" className="mb-3.5">
        <Stat
          surface
          label="services up"
          value={total ? <AnimatedNumber value={up} /> : "—"}
          unit={total ? `/ ${total}` : undefined}
          tone={allOk ? "ok" : down > 0 ? "crit" : undefined}
          foot={<span className="faint">{allOk ? "all containers healthy" : `${down} unhealthy`}</span>}
        />
        <Stat
          surface
          label="down"
          value={<AnimatedNumber value={down} />}
          tone={down > 0 ? "crit" : "ok"}
          foot={<span className="faint">failing health checks</span>}
        />
        <Stat surface label="checked" value={checked} foot={<span className="faint">auto-refresh every 5s</span>} />
        <Stat
          surface
          label="stack"
          value="qcs"
          unit={`v${__APP_VERSION__}`}
          foot={<span className="faint">single server · one image</span>}
        />
      </Grid>

      <Panel>
        <Panel.Head title="Services">
          <div className="flex-1" />
          <span className="num faint text-[11px]">{total} containers · compose stack qcom-scrape</span>
        </Panel.Head>
        <Panel.Body tight>
          {isLoading ? (
            <SkeletonRegion label="Loading services">
              <Stack gap="sm" className="p-4">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} h={28} />
                ))}
              </Stack>
            </SkeletonRegion>
          ) : isError ? (
            <ErrorState
              compact
              title="Could not reach the status endpoint"
              message="Is the frontend server running?"
              onRetry={() => refetch()}
            />
          ) : services.length === 0 ? (
            <EmptyState compact title="No services reported" />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Status</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s) => {
                  const ok = s.status === "ok";
                  return (
                    <tr key={s.name}>
                      <td className="strong mono">{s.name}</td>
                      <td>
                        <Badge variant={toneVariant(ok ? "ok" : s.status === "down" ? "crit" : "idle")}>
                          <Dot tone={ok ? "ok" : s.status === "down" ? "crit" : undefined} live={ok} />
                          {ok ? "healthy" : s.status}
                        </Badge>
                      </td>
                      <td>
                        <span className="id">{s.detail ?? "—"}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Panel.Body>
      </Panel>
    </>
  );
}
